import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Plus, IndianRupee, TrendingUp, Clock, FileText,
  Download, Send, CheckCircle2, AlertCircle, Calendar,
  Filter, Search, CreditCard
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const revenueData = [
  { month: 'Jul', revenue: 180000 },
  { month: 'Aug', revenue: 220000 },
  { month: 'Sep', revenue: 195000 },
  { month: 'Oct', revenue: 280000 },
  { month: 'Nov', revenue: 310000 },
  { month: 'Dec', revenue: 290000 },
];

const invoices = [
  { id: 'INV-001', client: 'Sharma Industries', amount: 85000, date: '15 Jan 2025', status: 'paid', dueDate: '30 Jan 2025' },
  { id: 'INV-002', client: 'Verma & Sons', amount: 45000, date: '12 Jan 2025', status: 'pending', dueDate: '27 Jan 2025' },
  { id: 'INV-003', client: 'ABC Corporation', amount: 150000, date: '10 Jan 2025', status: 'overdue', dueDate: '20 Jan 2025' },
  { id: 'INV-004', client: 'Gupta Holdings', amount: 72000, date: '08 Jan 2025', status: 'paid', dueDate: '23 Jan 2025' },
];

const statusColors = {
  paid: 'bg-green-500/20 text-green-500',
  pending: 'bg-yellow-500/20 text-yellow-500',
  overdue: 'bg-red-500/20 text-red-500',
};

const Billing = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <MainLayout title="Billing & Invoices" subtitle="Manage your finances">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Revenue', value: '₹14.8L', change: '+18%', icon: IndianRupee, color: 'green' },
          { label: 'Pending', value: '₹2.4L', change: '8 invoices', icon: Clock, color: 'yellow' },
          { label: 'Overdue', value: '₹1.5L', change: '3 invoices', icon: AlertCircle, color: 'red' },
          { label: 'This Month', value: '₹3.1L', change: '+12%', icon: TrendingUp, color: 'blue' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="glass-card p-6 relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
            <motion.div
              className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-${stat.color}-500/20 blur-2xl`}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <h3 className="text-3xl font-bold mt-1">{stat.value}</h3>
                <p className={`text-sm mt-1 text-${stat.color}-500`}>{stat.change}</p>
              </div>
              <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
          {['overview', 'invoices', 'time-tracking', 'expenses'].map((tab) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                activeTab === tab ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' : ''
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {tab.replace('-', ' ')}
            </motion.button>
          ))}
        </div>
        <motion.button
          className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          <span>New Invoice</span>
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-3 gap-6"
          >
            {/* Revenue Chart */}
            <div className="col-span-2 glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold">Revenue Trend</h3>
                  <p className="text-sm text-gray-500">Last 6 months</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2D8CFF" />
                      <stop offset="100%" stopColor="#1E3A5F" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#E5E7EB'} />
                  <XAxis dataKey="month" stroke={isDark ? '#94A3B8' : '#6B7280'} />
                  <YAxis stroke={isDark ? '#94A3B8' : '#6B7280'} />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? '#1E293B' : '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Quick Actions */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-6"
              >
                <h3 className="font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Create Invoice', icon: Plus, color: 'blue' },
                    { label: 'Record Payment', icon: CreditCard, color: 'green' },
                    { label: 'Generate Report', icon: FileText, color: 'purple' },
                  ].map((action) => (
                    <motion.button
                      key={action.label}
                      className="w-full p-4 rounded-xl flex items-center gap-3 hover:bg-gray-500/10 transition-all"
                      whileHover={{ x: 5 }}
                    >
                      <div className={`p-2 rounded-lg bg-${action.color}-500/20`}>
                        <action.icon className={`w-5 h-5 text-${action.color}-500`} />
                      </div>
                      <span>{action.label}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
              >
                <h3 className="font-semibold mb-4">Payment Methods</h3>
                <div className="space-y-3">
                  {['UPI', 'Bank Transfer', 'Card', 'Cash'].map((method) => (
                    <div key={method} className="flex items-center justify-between p-3 rounded-xl bg-gray-500/5">
                      <span className="text-sm">{method}</span>
                      <span className="text-xs text-green-500">Active</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {activeTab === 'invoices' && (
          <motion.div
            key="invoices"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card overflow-hidden"
          >
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Invoice #</th>
                  <th className="px-6 py-4 text-left">Client</th>
                  <th className="px-6 py-4 text-left">Amount</th>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Due Date</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <motion.tr
                    key={invoice.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5"
                  >
                    <td className="px-6 py-4 font-semibold text-blue-500">{invoice.id}</td>
                    <td className="px-6 py-4">{invoice.client}</td>
                    <td className="px-6 py-4 font-semibold">₹{invoice.amount.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gray-500">{invoice.date}</td>
                    <td className="px-6 py-4 text-gray-500">{invoice.dueDate}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${statusColors[invoice.status]}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <motion.button className="p-2 rounded-lg hover:bg-gray-500/10" whileHover={{ scale: 1.1 }}>
                          <Download className="w-4 h-4" />
                        </motion.button>
                        <motion.button className="p-2 rounded-lg hover:bg-gray-500/10" whileHover={{ scale: 1.1 }}>
                          <Send className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}

        {(activeTab === 'time-tracking' || activeTab === 'expenses') && (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card p-12 text-center"
          >
            <Clock className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h3 className="text-xl font-semibold mb-2">{activeTab === 'time-tracking' ? 'Time Tracking' : 'Expenses'}</h3>
            <p className="text-gray-500">This feature is coming soon</p>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default Billing;
